import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Title,
    Stack,
    Textarea,
    FileInput,
    Button,
    Card,
    Progress,
    useMantineTheme,
} from '@mantine/core';
import { useForm, Controller } from 'react-hook-form';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconCheck, IconX } from '@tabler/icons-react';
import { PackageService } from '../../types';

interface ExpectedPackageFormValues {
    note: string;
    invoice_image: File | null;
}

export function ExpectedPackageForm({ closeModal }: { closeModal?: () => void } = {}) {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid, isSubmitting }
    } = useForm<ExpectedPackageFormValues>({
        mode: 'onChange',
        defaultValues: {
            note: '',
            invoice_image: null,
        }
    });

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [fileBase64, setFileBase64] = useState<string | null>(null);
    const theme = useMantineTheme();
    const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    // Function to convert File to base64
    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const simulateProgress = () => {
        uploadIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 95) {
                    if (uploadIntervalRef.current) {
                        clearInterval(uploadIntervalRef.current);
                    }
                    return 95;
                }
                return prev + 5;
            });
        }, 200);
    };

    // Handle file selection and conversion to base64
    const handleFileChange = async (file: File | null) => {
        if (file) {
            try {
                const base64String = await convertToBase64(file);
                setFileBase64(base64String);
                return file; // Return the file for the form controller
            } catch (error) {
                notifications.show({
                    title: 'Error',
                    message: 'Failed to process the selected file',
                    color: 'red',
                    icon: <IconX size={18} />,
                });
                setFileBase64(null);
                return null;
            }
        } else {
            setFileBase64(null);
            return null;
        }
    };

    const onSubmit = handleSubmit(async (values) => {
        setLoading(true);
        setProgress(0);
        simulateProgress();

        try {
            if (!fileBase64) {
                throw new Error('Please upload an invoice image');
            }

            // Create the request object with the expected structure
            const requestData: any = {
                note: values.note,
                invoice_image: fileBase64 as unknown as Blob, // Cast the base64 string to Blob
            };

            // Send data using the proper format
            await PackageService.postPackageExpect(requestData);

            // Complete the progress bar
            setProgress(100);

            notifications.show({
                title: 'Success',
                message: 'Your package has been successfully registered in our system. We will notify you once it arrives.',
                color: 'green',
                withCloseButton: true,
                icon: <IconCheck size={18} />,
            });

            // Reset form
            reset();

            // Close modal if provided
            if (closeModal) closeModal();

        } catch (error) {
            let errorMessage = 'Failed to submit package expectation.';

            // Handle specific error types
            if (error instanceof Error) {
                if (error.message.includes('Authentication required')) {
                    errorMessage = 'Please log in to submit an expected package.';
                } else {
                    errorMessage = error.message;
                }
            }

            notifications.show({
                title: 'Error',
                message: errorMessage,
                color: 'red',
                icon: <IconX size={18} />,
            });
        } finally {
            if (uploadIntervalRef.current) {
                clearInterval(uploadIntervalRef.current);
            }
            setLoading(false);
            navigate('/expected');
        }
    });

    return (
        <Card withBorder shadow="sm" radius="md" p="lg" h="100%">
            <Title order={3} size="h4" mb="md">Submit Expected Package</Title>

            <form onSubmit={onSubmit}>
                <Stack gap="md">
                    <Controller
                        name="note"
                        control={control}
                        rules={{
                            required: 'Package details are required',
                            minLength: {
                                value: 5,
                                message: 'Please provide more details (minimum 5 characters)'
                            }
                        }}
                        render={({ field }) => (
                            <Textarea
                                label="Note"
                                placeholder="Add details about your expected package..."
                                minRows={3}
                                error={errors.note?.message}
                                {...field}
                            />
                        )}
                    />

                    <Controller
                        name="invoice_image"
                        control={control}
                        rules={{ required: 'Invoice is required' }}
                        render={({ field }) => (
                            <FileInput
                                variant="filled"
                                radius="md"
                                label="Select Invoice"
                                description="Upload your invoice file (PDF, JPG, PNG)"
                                placeholder="Select file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                leftSection={<IconUpload size={16} />}
                                error={errors.invoice_image?.message}
                                value={field.value}
                                onChange={async (file) => {
                                    const processedFile = await handleFileChange(file);
                                    field.onChange(processedFile);
                                }}
                            />
                        )}
                    />

                    {loading && (
                        <Progress
                            value={progress}
                            color={progress === 100 ? 'green' : theme.primaryColor}
                            striped
                            animated={progress < 100}
                            mb="xs"
                        />
                    )}

                    <Button
                        type="submit"
                        loading={loading || isSubmitting}
                        mt="sm"
                        disabled={loading || !isValid}
                    >
                        Submit
                    </Button>

                    <div style={{ color: '#868E96', fontSize: '14px' }}>
                        <span>
                            Notify us of your order, attach the invoice, and submit details to ensure proper package handling.
                        </span>
                    </div>
                </Stack>
            </form>
        </Card >
    );
}
